import { hostOf, externalHref } from '../lib/url';
import Info from './Glossary';

interface P { query: string; state: string; competitors?: string[]; citations?: string[] }

const searchUrl = (name: string): string => `https://www.google.com/search?q=${encodeURIComponent(name)}`;

/**
 * The run-level answer to "so who IS winning?".
 *
 * Each question already listed the brands named in its own answer, but nothing
 * added them up — so a rival appearing in fourteen answers looked exactly like
 * one appearing once. History has had this across runs for a while; a single
 * run deserves it too, and now the two places say the same thing.
 */
export default function RunRivals({ probes, domain }: { probes: P[]; domain: string }): JSX.Element | null {
  const own = (h: string): boolean => Boolean(domain) && (h === domain || h.endsWith(`.${domain}`));

  const rivalCount = new Map<string, number>();
  const lossHosts = new Map<string, number>();
  for (const p of probes) {
    for (const c of new Set(p.competitors ?? [])) rivalCount.set(c, (rivalCount.get(c) ?? 0) + 1);
    if (p.state === 'mentioned') continue;
    for (const h of new Set((p.citations ?? []).map(hostOf).filter((h) => h && !own(h)))) {
      lossHosts.set(h, (lossHosts.get(h) ?? 0) + 1);
    }
  }
  const rivals = [...rivalCount.entries()].sort((a, b) => b[1] - a[1]);
  const sources = [...lossHosts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15);
  if (rivals.length === 0 && sources.length === 0) return null;

  const total = probes.length;
  const top = rivals[0];

  return (
    <div className="w-full max-w-5xl border border-warm-dark-2 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-serif text-xl">Who AI names instead of you</h3>
        <Info term="share_of_voice" />
      </div>
      <p className="text-sm text-text-secondary mb-4">
        Across all {total} {total === 1 ? 'question' : 'questions'} in this run.
        {top && <> The name to beat is <span className="text-text-primary">{top[0]}</span>, in {top[1]} of them.</>}
      </p>

      {rivals.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-xs text-left">
              <th className="py-1 pr-3">Named</th><th className="pr-3 w-24">Questions</th><th>Share of this run</th>
            </tr></thead>
            <tbody>
              {rivals.map(([name, n]) => (
                <tr key={name} className="border-t border-warm-dark-2">
                  <td className="py-1.5 pr-3">
                    <a href={searchUrl(name)} target="_blank" rel="noreferrer" className="text-text-primary hover:text-cyan transition">
                      {name} ↗
                    </a>
                  </td>
                  <td className="pr-3 font-mono text-text-secondary">{n}/{total}</td>
                  <td>
                    <span className="inline-flex items-center gap-2 w-full">
                      <span className="h-1.5 rounded-full bg-[#E0574A]/70" style={{ width: `${Math.max(4, (n / total) * 160)}px` }} />
                      <span className="font-mono text-xs text-text-secondary">{Math.round((n / total) * 100)}%</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-warm-dark-2">
          <p className="text-sm text-text-primary">The pages that beat you</p>
          <p className="text-sm text-text-secondary mb-2">
            Cited in the answers you did not win. Not a generic directory list. These are the domains AI actually reads
            for <em>your</em> questions. Get named on them, or publish something better than what they have.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sources.map(([host, n]) => (
              <a
                key={host}
                href={externalHref(host)}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs border border-[#E0574A]/30 rounded px-2 py-1 text-text-secondary hover:text-cyan hover:border-cyan/50 transition"
              >
                {host}<span className="text-[#E0574A]/80"> ×{n}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
